import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Users, BadgeDollarSign, Flag, Settings as SettingsIcon,
  Ban, CircleCheck, CircleX, ShieldCheck, Search, Radio, ChevronRight,
} from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";
import { fmtNum, timeAgo } from "../config";
import { Spinner, Empty, Avatar, SectionTitle } from "../components/cards";

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-white/10 bg-white/[0.02] p-5 ${className}`}>{children}</div>
);

const Pill = ({ tone = "slate", children, testId }) => {
  const tones = {
    slate: "bg-white/10 text-slate-300",
    green: "bg-emerald-500/15 text-emerald-400",
    red: "bg-crimson/15 text-crimson",
    amber: "bg-amber-500/15 text-amber-400",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-wider ${tones[tone]}`} data-testid={testId}>
      {children}
    </span>
  );
};

const statusTone = (s) => (s === "approved" || s === true ? "green" : s === "pending" ? "amber" : s === "rejected" || s === "suspended" ? "red" : "slate");

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "users", label: "Users", icon: Users },
  { key: "monetization", label: "Monetization", icon: BadgeDollarSign },
  { key: "reports", label: "Reports", icon: Flag },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

const StatCard = ({ label, value, testId, accent }) => (
  <Card className="text-center">
    <p className={`font-heading text-3xl font-black ${accent || "text-white"}`} data-testid={testId}>{value}</p>
    <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
  </Card>
);

const Overview = () => {
  const [ov, setOv] = useState(null);
  useEffect(() => {
    api.get("/admin/overview").then(setOv).catch(() => setOv({}));
  }, []);
  if (ov === null) return <Spinner />;
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6" data-testid="admin-overview">
      <StatCard label="Users" value={fmtNum(ov.users)} testId="stat-users" />
      <StatCard label="Videos" value={fmtNum(ov.videos)} testId="stat-videos" />
      <StatCard label="Tracks" value={fmtNum(ov.tracks)} testId="stat-tracks" />
      <StatCard label="Pending reports" value={fmtNum(ov.pendingReports)} testId="stat-pending-reports" accent="text-amber-400" />
      <StatCard label="Pending monetization" value={fmtNum(ov.pendingMonetization)} testId="stat-pending-monetization" accent="text-amber-400" />
      <StatCard label="Suspended" value={fmtNum(ov.suspended)} testId="stat-suspended" accent="text-crimson" />
    </div>
  );
};

const UsersTab = () => {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setUsers(Array.isArray(r) ? r : [])).catch(() => setUsers([]));
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!users) return [];
    const t = q.trim().toLowerCase();
    if (!t) return users;
    return users.filter(
      (u) =>
        (u.displayName || "").toLowerCase().includes(t) ||
        (u.username || "").toLowerCase().includes(t) ||
        (u.email || "").toLowerCase().includes(t)
    );
  }, [users, q]);

  const toggleSuspend = async (u) => {
    setBusy(u.id);
    try {
      const r = await api.post(`/admin/users/${u.id}/suspend`, { suspended: !u.suspended });
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, suspended: r.suspended ?? !u.suspended } : x)));
    } catch {}
    setBusy(null);
  };

  if (users === null) return <Spinner />;

  return (
    <div data-testid="admin-users">
      <div className="mb-5 flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2.5">
        <Search size={16} className="shrink-0 text-slate-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search users by name, username or email"
          data-testid="admin-user-search"
          className="w-full bg-transparent text-sm text-white placeholder-slate-600 outline-none"
        />
      </div>
      {filtered.length === 0 ? (
        <Empty>No users found.</Empty>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/10">
          <div className="hidden grid-cols-[1.6fr_1.2fr_1fr_0.8fr_1fr] gap-3 border-b border-white/10 bg-white/[0.03] px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500 md:grid">
            <span>User</span><span>Email</span><span>Monetization</span><span>Status</span><span className="text-right">Action</span>
          </div>
          {filtered.map((u) => (
            <div key={u.id} className="flex flex-col gap-3 border-b border-white/5 px-4 py-3 last:border-0 md:grid md:grid-cols-[1.6fr_1.2fr_1fr_0.8fr_1fr] md:items-center md:gap-3" data-testid={`admin-user-${u.id}`}>
              <div className="flex min-w-0 items-center gap-3">
                <Avatar user={u} size={34} />
                <div className="min-w-0">
                  <p className="truncate font-heading text-sm font-semibold text-white">{u.displayName || u.username}</p>
                  <p className="truncate text-xs text-slate-500">@{u.username}</p>
                </div>
                {u.isAdmin && <ShieldCheck size={14} className="shrink-0 text-crimson" title="Admin" />}
              </div>
              <p className="truncate text-xs text-slate-400">{u.email}</p>
              <div><Pill tone={statusTone(u.monetizationStatus)} testId={`user-monetization-${u.id}`}>{u.monetizationStatus || "none"}</Pill></div>
              <div><Pill tone={u.suspended ? "red" : "green"} testId={`user-status-${u.id}`}>{u.suspended ? "Suspended" : "Active"}</Pill></div>
              <div className="md:text-right">
                {!u.isAdmin && (
                  <button
                    onClick={() => toggleSuspend(u)}
                    disabled={busy === u.id}
                    data-testid={`user-suspend-${u.id}`}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-heading text-[11px] font-bold transition-colors ${
                      u.suspended
                        ? "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                        : "border-crimson/40 text-crimson hover:bg-crimson/10"
                    } disabled:opacity-50`}
                  >
                    {u.suspended ? <CircleCheck size={13} /> : <Ban size={13} />}
                    {u.suspended ? "Unsuspend" : "Suspend"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MonetizationTab = () => {
  const [creators, setCreators] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/admin/monetization").then((r) => setCreators(Array.isArray(r) ? r : [])).catch(() => setCreators([]));
  }, []);

  const decide = async (c, status) => {
    setBusy(c.id + status);
    try {
      await api.post("/admin/monetization/requests", { userId: c.id, status });
      setCreators((list) => list.map((x) => (x.id === c.id ? { ...x, monetizationStatus: status, monetized: status === "approved" } : x)));
    } catch {}
    setBusy(null);
  };

  if (creators === null) return <Spinner />;
  const pending = creators.filter((c) => c.monetizationStatus === "pending");
  const others = creators.filter((c) => c.monetizationStatus !== "pending");

  const Row = ({ c }) => (
    <Card className="flex flex-col gap-4 md:flex-row md:items-center" data-testid={`admin-monetization-${c.id}`}>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Avatar user={c} size={40} />
        <div className="min-w-0">
          <p className="truncate font-heading text-sm font-semibold text-white">{c.displayName || c.username}</p>
          <p className="truncate text-xs text-slate-500">@{c.username} · {fmtNum(c.subscribers)} subscribers</p>
        </div>
      </div>
      {c.earnings && (
        <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4">
          <div><p className="font-heading text-base font-bold text-white">{fmtNum(c.earnings.subscribers)}</p><p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Subs / {fmtNum(c.earnings.thresholds?.subscribers)}</p></div>
          <div><p className="font-heading text-base font-bold text-white">{fmtNum(Math.round(c.earnings.watchHours))}</p><p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Watch hrs / {fmtNum(c.earnings.thresholds?.watchHours)}</p></div>
          <div><p className="font-heading text-base font-bold text-white">{fmtNum(c.earnings.monetizedViews)}</p><p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Monetized views</p></div>
          <div><p className="font-heading text-base font-bold text-emerald-400">${(c.earnings.revenue ?? 0).toFixed(2)}</p><p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Revenue</p></div>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Pill tone={statusTone(c.monetizationStatus)} testId={`monetization-status-${c.id}`}>{c.monetizationStatus || "none"}</Pill>
        {c.monetizationStatus === "pending" && (
          <>
            <button onClick={() => decide(c, "approved")} disabled={busy} data-testid={`monetization-approve-${c.id}`} className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 px-3.5 py-1.5 font-heading text-[11px] font-bold text-emerald-400 transition-colors hover:bg-emerald-500/10 disabled:opacity-50">
              <CircleCheck size={13} /> Approve
            </button>
            <button onClick={() => decide(c, "rejected")} disabled={busy} data-testid={`monetization-reject-${c.id}`} className="inline-flex items-center gap-1 rounded-full border border-crimson/40 px-3.5 py-1.5 font-heading text-[11px] font-bold text-crimson transition-colors hover:bg-crimson/10 disabled:opacity-50">
              <CircleX size={13} /> Reject
            </button>
          </>
        )}
      </div>
    </Card>
  );

  return (
    <div data-testid="admin-monetization">
      <SectionTitle>Pending applications ({pending.length})</SectionTitle>
      {pending.length === 0 ? <Empty>No pending monetization applications.</Empty> : <div className="space-y-3">{pending.map((c) => <Row key={c.id} c={c} />)}</div>}
      <div className="mt-10">
        <SectionTitle>All monetized creators</SectionTitle>
        {others.length === 0 ? <Empty>No other creators.</Empty> : <div className="space-y-3">{others.map((c) => <Row key={c.id} c={c} />)}</div>}
      </div>
    </div>
  );
};

const ReportsTab = () => {
  const [reports, setReports] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/admin/reports").then((r) => setReports(Array.isArray(r) ? r : [])).catch(() => setReports([]));
  }, []);

  const resolve = async (r) => {
    setBusy(r.id);
    try {
      await api.post(`/admin/reports/${r.id}/resolve`);
      setReports((list) => list.map((x) => (x.id === r.id ? { ...x, status: "resolved" } : x)));
    } catch {}
    setBusy(null);
  };

  if (reports === null) return <Spinner />;
  const pending = reports.filter((r) => r.status === "pending");
  const done = reports.filter((r) => r.status !== "pending");

  const Row = ({ r }) => (
    <Card className="flex flex-col gap-3 md:flex-row md:items-center" data-testid={`admin-report-${r.id}`}>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Avatar user={r.reporter} size={36} />
        <div className="min-w-0">
          <p className="truncate text-sm text-white"><span className="font-semibold">{r.reporter?.displayName || r.reporter?.username}</span> reported a {r.targetType}</p>
          <p className="truncate text-xs text-slate-500">{r.reason}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500">{timeAgo(r.createdAt)}</span>
        <Pill tone={r.status === "pending" ? "amber" : "green"} testId={`report-status-${r.id}`}>{r.status}</Pill>
        {r.status === "pending" && (
          <button onClick={() => resolve(r)} disabled={busy === r.id} data-testid={`report-resolve-${r.id}`} className="rounded-full border border-emerald-500/40 px-3.5 py-1.5 font-heading text-[11px] font-bold text-emerald-400 transition-colors hover:bg-emerald-500/10 disabled:opacity-50">
            Resolve
          </button>
        )}
      </div>
    </Card>
  );

  return (
    <div data-testid="admin-reports">
      <SectionTitle>Pending ({pending.length})</SectionTitle>
      {pending.length === 0 ? <Empty>No pending reports.</Empty> : <div className="space-y-3">{pending.map((r) => <Row key={r.id} r={r} />)}</div>}
      {done.length > 0 && (
        <div className="mt-10">
          <SectionTitle>Resolved</SectionTitle>
          <div className="space-y-3">{done.map((r) => <Row key={r.id} r={r} />)}</div>
        </div>
      )}
    </div>
  );
};

const SettingsTab = () => {
  const [subs, setSubs] = useState("");
  const [hours, setHours] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    api.get("/admin/overview")
      .then((ov) => {
        setSubs(String(ov.thresholds?.subscribers ?? ""));
        setHours(String(ov.thresholds?.watchHours ?? ""));
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setNote("");
    try {
      await api.post("/admin/settings/thresholds", { subscribers: parseInt(subs, 10), watchHours: parseInt(hours, 10) });
      setNote("Thresholds updated.");
    } catch (err) {
      setNote(err.message || "Failed to save.");
    }
    setSaving(false);
  };

  if (!loaded) return <Spinner />;

  return (
    <div data-testid="admin-settings" className="max-w-md">
      <Card>
        <SectionTitle>Monetization thresholds</SectionTitle>
        <p className="mb-5 text-sm leading-relaxed text-slate-400">
          The minimum a creator needs before they can apply for monetization. This is shared with the mobile app.
        </p>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Subscribers required</label>
            <input type="number" min="0" value={subs} onChange={(e) => setSubs(e.target.value)} data-testid="threshold-subscribers" className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white outline-none focus:border-crimson/60" />
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Watch hours required</label>
            <input type="number" min="0" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="threshold-watchhours" className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white outline-none focus:border-crimson/60" />
          </div>
          <button type="submit" disabled={saving} data-testid="thresholds-save" className="rounded-full bg-crimson px-6 py-2.5 font-heading text-xs font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50">
            {saving ? "Saving…" : "Save thresholds"}
          </button>
          {note && <p className="text-sm text-slate-400" data-testid="thresholds-note">{note}</p>}
        </form>
      </Card>
    </div>
  );
};

export const AdminPanel = () => {
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("overview");

  if (!ready) return <Spinner />;
  if (!user || !(user.isAdmin || user.isSuperAdmin)) {
    return (
      <div className="mx-auto max-w-md py-24 text-center" data-testid="admin-forbidden">
        <ShieldCheck size={40} className="mx-auto text-slate-700" />
        <h1 className="mt-4 font-heading text-xl font-bold text-white">Admins only</h1>
        <p className="mt-2 text-sm text-slate-500">You need an administrator account to open the control panel.</p>
        <Link to="/app" className="mt-6 inline-block rounded-full bg-crimson px-6 py-2.5 font-heading text-xs font-bold text-white hover:bg-crimson-hover" data-testid="admin-forbidden-home">
          Back to Home
        </Link>
      </div>
    );
  }

  const Active = { overview: Overview, users: UsersTab, monetization: MonetizationTab, reports: ReportsTab, settings: SettingsTab }[tab];

  return (
    <div className="mx-auto max-w-6xl" data-testid="admin-panel">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-crimson/15 text-crimson"><ShieldCheck size={20} /></span>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-white">Admin control panel</h1>
          <p className="text-sm text-slate-500">Manage Michael C Streamline — same controls as the mobile app.</p>
        </div>
      </div>

      <div className="mb-8 flex gap-1 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02] p-1" data-testid="admin-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`admin-tab-${key}`}
            className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 font-heading text-xs font-bold transition-colors ${
              tab === key ? "bg-crimson text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      <Active />
    </div>
  );
};

export default AdminPanel;
